"use client";

import { StateCreator } from "zustand";
import { StoreState } from "./useBoundStore";
import { Surgery } from "@clients/velys-infrastructure/api";
import config from "@/config";
import { AuthState } from "./createAuthSlice";
import { initialCategorizedActions } from "./createCarePlanSlice";

// this slice allows use of all states & actions from all slices

export type ActionState = {
  // Actions
  handleCloseModal: () => void;
  handleViewImages: (patient: Surgery | null) => void;
  handleCloseViewPatient: () => void;
  handleViewPatientData: (patient: Surgery | null) => void;
  handleEditPatient: () => void;
  handleCloseEditPatient: () => void;
  handleAddPatient: () => void;
  handleCloseAddPatient: () => void;
  handleReturnToPatientPath: () => void;
  signOutRedirect: (isManual?: boolean) => void;
};

export const createActionSlice: StateCreator<
  StoreState & AuthState,
  [],
  [],
  ActionState
> = (set, get) => ({
  // Actions
  handleCloseModal: () => {
    get().setModalOpen(false);
    get().setModalType("");
  },
  handleViewImages: (patient) => {
    get().setModalOpen(true);
    get().setModalType("view-images");
    get().setSurgery(patient);
  },

  handleCloseViewPatient: () => {
    get().handleCloseModal();
    get().setSurgery(null);
  },

  handleViewPatientData: (patient) => {
    get().setModalOpen(true);
    get().setModalType("view-patient-data");
    get().setSurgery(patient);
  },

  handleEditPatient: () => {
    get().setModalOpen(true);
    get().setModalType("edit-patient");
    get().setEnrollmentStep("careplan");
  },

  handleCloseEditPatient: () => {
    get().handleCloseModal();
    get().setSelectedAction(null);
    get().setIsAIOpen(false);
    get().setEnrollmentStep("patient");
    get().setSortedCarePlanActions(initialCategorizedActions);
  },

  handleAddPatient: () => {
    get().setModalOpen(true);
    get().setModalType("add-patient");
    get().setCurrentCarePlanId(null);
    get().setCurrentCarePlan(null);
  },

  handleCloseAddPatient: () => {
    get().handleCloseModal();
    get().setCurrentCarePlan(null);
    get().setCurrentCarePlanId(null);
    get().setIsCreateAction(false);
    get().setSelectedAction(null);
    get().setIsAIOpen(false);
    get().setEnrollmentStep("patient");
    get().setPendingEnrollmentId("");
    get().setSortedCarePlanActions(initialCategorizedActions);
  },

  handleReturnToPatientPath: () => {
    get().setPatientEnrollment(null);
    get().setCurrentCarePlan(null);
    get().setCurrentCarePlanId(null);
  },

  signOutRedirect: (isManual?: boolean) => {
    const { clientId, logoutUri, cognitoDomain } = config.cognito;

    // Reset user access token and profile using store actions
    get().setUserToken(null);
    get().setUserProfile(null);
    // Remove persisted data
    localStorage.removeItem("auth-storage");
    localStorage.removeItem("enrollmentId");
    localStorage.removeItem("downloadId");
    localStorage.removeItem("phone-banner-dismissed");

    // clear session cookies
    if (typeof window !== "undefined") {
      const isSecure = window.location.hostname !== "localhost";
      document.cookie = `user-locale-preference=; path=/; max-age=0; SameSite=Lax${
        isSecure ? "; Secure" : ""
      }`;
    }
    sessionStorage.removeItem("user-country");
    sessionStorage.removeItem("geo-error");

    // Clear all oidc.user from sessionStorage
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("oidc."))
      .forEach((key) => sessionStorage.removeItem(key));

    // check if manual logout
    if (isManual) {
      sessionStorage.removeItem("app_session");
      localStorage.removeItem("had_session");
      localStorage.setItem("manual_logout", "true");
    }

    get().clearGeoLocation();

    setTimeout(() => {
      window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    }, 100);
  },
});
